import {
  BILLING_FILENAME_PREFIX,
  BILLING_INVOICE_PREFIX,
} from "@/config/billing";

const STORAGE_KEY = "pizza-admin:billing-invoice-counter:v1";

const ANCHOR_YEAR = 2026;
const ANCHOR_MONTH_INDEX = 5;
const ANCHOR_SERIAL = 7;

type CounterState = Record<string, number>;

const isCounterState = (value: unknown): value is CounterState => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return Object.values(value as Record<string, unknown>).every(
    (entry) => typeof entry === "number" && Number.isFinite(entry),
  );
};

const readCounterState = (): CounterState => {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    return isCounterState(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeCounterState = (state: CounterState): void => {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / privacy-mode failures
  }
};

export interface InvoiceNumber {
  serial: number;
  short: string;
  variableSymbol: string;
  filename: string;
}

const padSerial = (serial: number): string =>
  String(serial).padStart(5, "0");

const monthKey = (year: number, monthIndex: number): string =>
  `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

const monthDistance = (
  year: number,
  monthIndex: number,
): number => {
  return (
    (year - ANCHOR_YEAR) * 12 + (monthIndex - ANCHOR_MONTH_INDEX)
  );
};

const anchorSerialFor = (year: number, monthIndex: number): number => {
  const distance = monthDistance(year, monthIndex);
  if (distance < 0) {
    return Math.max(1, ANCHOR_SERIAL + distance);
  }
  return ANCHOR_SERIAL + distance;
};

export const getNextInvoiceNumber = (
  year: number,
  monthIndex: number,
): InvoiceNumber => {
  const state = readCounterState();
  const key = monthKey(year, monthIndex);
  const lastSerial = state[key];
  let serial: number;

  if (lastSerial === undefined) {
    serial = anchorSerialFor(year, monthIndex);
  } else {
    serial = lastSerial + 1;
  }

  state[key] = serial;
  writeCounterState(state);

  const yearShort = String(year).slice(-2);
  const short = `${yearShort}${BILLING_INVOICE_PREFIX}${padSerial(serial)}`;
  const variableSymbol = `${yearShort}${padSerial(serial).slice(1)}`;
  const filename = `${BILLING_FILENAME_PREFIX}_${short}.pdf`;

  return { serial, short, variableSymbol, filename };
};

export const peekNextInvoiceNumber = (
  year: number,
  monthIndex: number,
): InvoiceNumber => {
  const state = readCounterState();
  const key = monthKey(year, monthIndex);
  const lastSerial = state[key];
  const serial =
    lastSerial === undefined
      ? anchorSerialFor(year, monthIndex)
      : lastSerial + 1;

  const yearShort = String(year).slice(-2);
  const short = `${yearShort}${BILLING_INVOICE_PREFIX}${padSerial(serial)}`;
  const variableSymbol = `${yearShort}${padSerial(serial).slice(1)}`;
  const filename = `${BILLING_FILENAME_PREFIX}_${short}.pdf`;
  return { serial, short, variableSymbol, filename };
};

export const resetInvoiceCounterForTests = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(STORAGE_KEY);
};